import { Box, ButtonGroup, Flex, Spinner, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';

import {
	IFilterBody,
	ISelectedFilterBody,
	ModulesType,
} from '../models/TableFilterModels';
import {
	clearLocalStorageFilter,
	setLocalStorageFilter,
} from '../utils/localStorageFilterManager';
import { SelectedFilterItem } from './SelectedFilterItem';

interface ISelectedFiltersProps {
	filtersMap: IFilterBody[];
	selectedItems: string[];
	handleFilterToggle: (itemId: string) => void;
	setShouldClearFilterForm: React.Dispatch<React.SetStateAction<boolean>>;
	shouldClearFilterForm: boolean;
	filters?: ISelectedFilterBody[];
	setFilters: React.Dispatch<React.SetStateAction<ISelectedFilterBody[]>>;
	module: ModulesType;
	setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>;
	getFromExternalAPI?: boolean;
	searchParams?: Object;
	localStorageValue: ISelectedFilterBody[];
	editable: boolean;
}

type RenderSelectedFilterItemParams = {
	itemId: string;
	filtersMap: IFilterBody[];
	handleRemoveItem: (itemId: string) => void;
	setForm: (form: ISelectedFilterBody[]) => void;
	form: ISelectedFilterBody[];
	applyFilters: (close: any) => void;
	module: ModulesType;
	selectedItems: string[];
	setSelectedItems: any;
};

type RenderSelectedFilterItemReturn = JSX.Element | null;

export const SelectedFilters = (props: ISelectedFiltersProps) => {
	const {
		filtersMap,
		selectedItems,
		handleFilterToggle,
		setShouldClearFilterForm,
		shouldClearFilterForm,
		filters = [],
		setFilters,
		module,
		setSelectedItems,
		getFromExternalAPI = false,
		searchParams,
		localStorageValue,
		editable = false,
	} = props;

	const [form, setForm] = useState<ISelectedFilterBody[]>([]);

	useEffect(() => {
		if (form?.length === 0 && localStorageValue?.length > 0) {
			setForm(localStorageValue);
			const selectedItems = localStorageValue
				.map((item: ISelectedFilterBody) => item.column_name)
				.filter(
					(value: string, index: number, self) =>
						self.indexOf(value) === index
				);

			setSelectedItems(selectedItems);

			if (!!filters?.length) {
				const isDifferent =
					JSON.stringify(filters) !==
					JSON.stringify(localStorageValue);
				if (isDifferent) {
					setFilters(localStorageValue);
				}
			} else {
				setFilters(localStorageValue);
			}
		}
	}, [form]);

	// Função que remove o filtro selecionado
	const handleRemoveItem = (itemId: string): void => {
		handleFilterToggle(itemId);
		const filterItemsByItemId = (
			prevItems: ISelectedFilterBody[],
			itemId: string
		) =>
			prevItems?.filter(
				(item: ISelectedFilterBody) => item.column_name !== itemId
			);

		setForm((prevForm: ISelectedFilterBody[]) =>
			filterItemsByItemId(prevForm, itemId)
		);
		setFilters((prevFilters: ISelectedFilterBody[]) =>
			filterItemsByItemId(prevFilters, itemId)
		);

		setLocalStorageFilter(
			module,
			editable,
			filterItemsByItemId(localStorageValue, itemId)
		);
	};

	// Função responsável por renderizar os itens selecionados no filtro
	const renderSelectedFilterItem = ({
		itemId,
		filtersMap,
		handleRemoveItem,
		form,
		applyFilters,
		module,
		selectedItems,
		setSelectedItems,
	}: RenderSelectedFilterItemParams): RenderSelectedFilterItemReturn => {
		const filterFind: IFilterBody | undefined = filtersMap.find(
			(filter: IFilterBody) => filter.column_name === itemId
		);

		if (!filterFind) {
			return null;
		}

		return (
			<SelectedFilterItem
				editable={editable}
				key={itemId}
				filter={filterFind}
				handleRemoveItem={handleRemoveItem}
				setForm={setForm}
				form={form}
				applyFilters={applyFilters}
				module={module}
				selectedItems={selectedItems}
				setSelectedItems={setSelectedItems}
				getFromExternalAPI={getFromExternalAPI}
				searchParams={searchParams}
				localStorageValue={localStorageValue}
			/>
		);
	};

	useEffect(() => {
		if (shouldClearFilterForm) {
			setForm([]);
			setFilters([]);
			setShouldClearFilterForm(false);
			clearLocalStorageFilter(module, editable);
		}
	}, [shouldClearFilterForm]);

	const applyFilters = useCallback(
		close => {
			const filterItemsMatching: ISelectedFilterBody[] | [] = form.filter(
				(filter: ISelectedFilterBody) => {
					return localStorageValue.some(
						(filterStorage: ISelectedFilterBody) => {
							return (
								filter.column_name ===
									filterStorage.column_name &&
								filter.operation === filterStorage.operation
							);
						}
					);
				}
			);

			if (localStorageValue.length > 0) {
				if (filterItemsMatching.length === 0) {
					if (form.length === 1) {
						setFilters(form);
						setLocalStorageFilter(module, editable, form);
					} else {
						setFilters(prev => [...prev, ...form]);
						setLocalStorageFilter(module, editable, [
							...localStorageValue,
							...form,
						]);
					}
				} else {
					const updateItemsOnMatching: ISelectedFilterBody[] =
						form.map((filter: ISelectedFilterBody) => {
							const matchingItem = filterItemsMatching.find(
								(filterStorage: ISelectedFilterBody) => {
									return (
										filterStorage.column_name ===
											filter.column_name &&
										filterStorage.operation ===
											filter.operation
									);
								}
							);
							return matchingItem || filter;
						});

					setFilters(updateItemsOnMatching);
					setLocalStorageFilter(
						module,
						editable,
						updateItemsOnMatching
					);
				}
			} else {
				setFilters(form);
				setLocalStorageFilter(module, editable, form);
			}

			close();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			form,
			localStorageValue,
			module,
			setFilters,
			setLocalStorageFilter,
			editable,
		]
	);

	const showSpinner = selectedItems.length > 0 && filtersMap.length === 0;

	return (
		<>
			{selectedItems.length > 0 && (
				<Box
					mt={4}
					w={'100%'}
				>
					<Text
						fontSize='sm'
						mb={2}
						color='easyBLUE.300'
					>
						Filtros selecionados:
					</Text>

					{showSpinner && (
						<Flex
							w={'100%'}
							alignItems={'center'}
						>
							<Text
								fontSize='sm'
								color='easyBLUE.300'
								textAlign={'center'}
							>
								Carregando filtros...
							</Text>
							<Spinner
								size='sm'
								color='easyBLUE.300'
								ml={2}
							/>
						</Flex>
					)}
					<ButtonGroup
						spacing={4}
						flexWrap={'wrap'}
					>
						{selectedItems.map(itemId => {
							return renderSelectedFilterItem({
								itemId,
								filtersMap,
								handleRemoveItem,
								setForm,
								form,
								applyFilters,
								module,
								selectedItems,
								setSelectedItems,
							});
						})}
					</ButtonGroup>
				</Box>
			)}
		</>
	);
};
